import {Typography} from '@hconnect/uikit'
import {Box, makeStyles, Theme} from '@material-ui/core'
import {AxiosError} from 'axios'
import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {BusinessLineType} from '../../../common/types'
import {useBranding} from '../../../Organisms/Branding'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {
  OrderIntakePayload,
  OrderRequestGroupResponse,
  OrderRequestResponse
} from '../../declarations/types'
import {ProcessingOrderCard} from '../ProcessingOrderCard'

import {CollectOrderSummaryItem} from './CollectOrderSummaryItem'

interface Props {
  isMobile: boolean
  showLoad: boolean
  showTime: boolean
  collection: OrderIntakePayload
  quantityUom: string
  itemOrder: string
  businessLine: BusinessLineType
  onError: (error: AxiosError) => void
  customErrorMessage?: string
  groupedOrderSummary: OrderRequestGroupResponse | undefined
  isLoading: boolean
  isError: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  errorContainer: {
    background: theme.palette.error.main,
    padding: theme.spacing(2)
  }
}))

export const CollectionProcessingOrderCard: React.FC<Props> = ({
  isMobile,
  showLoad,
  showTime,
  collection,
  quantityUom,
  groupedOrderSummary,
  customErrorMessage,
  onError,
  isLoading
}) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const {dispatchPhoneNumber} = useBranding()
  const [orderSummaryData, setOrderSummaryData] = React.useState<OrderRequestResponse | null>(null)
  const [showError, setShowError] = React.useState(false)

  useEffect(() => {
    if (groupedOrderSummary && groupedOrderSummary.isSuccess) {
      setShowError(false)
      setOrderSummaryData(groupedOrderSummary.result as OrderRequestResponse)
    } else if (groupedOrderSummary && !groupedOrderSummary.isSuccess) {
      setShowError(true)
      onError(groupedOrderSummary.result as AxiosError)
    }
  }, [collection])

  return (
    <>
      {showError ? (
        <ProcessingOrderCard
          shippingType={ShippingType.COLLECT}
          isLoading={isLoading}
          isMobile={isMobile}
          showLoad={showLoad}
          showTime={showTime}
          loadingLabel={t('orderIntake.processingOrder')}
          date={collection.deliveryDate}
          time={collection.deliveryTime}
          quantity={collection.capacity.quantity}
          quantityType={collection.capacity.quantityType}
          quantityUom={quantityUom}
          haulierInformation={collection.haulerInfo}
          customerReference={collection.customerReference}
          driverInstructions={collection.additionalDriverInfo?.driverInstructions}
          hasError={showError}
          errorMessage={
            <Box className={classes.errorContainer}>
              <Typography variant="h3" customColor="textErrorContrastText">
                {t('orderIntake.orderFailed')}
              </Typography>
              {customErrorMessage ? (
                <Typography variant="body2" customColor="textErrorContrastText">
                  {customErrorMessage}
                </Typography>
              ) : null}
              <Typography variant="h3" customColor="textErrorContrastText">
                {dispatchPhoneNumber
                  ? t('orderIntake.contactByPhone', {phoneNumber: dispatchPhoneNumber})
                  : t('orderIntake.contactByPhoneWithoutNumber')}
              </Typography>
              <Typography variant="body2" customColor="textErrorContrastText">
                {t('orderIntake.errorMessage')}
              </Typography>
            </Box>
          }
        />
      ) : orderSummaryData ? (
        <CollectOrderSummaryItem
          isMobile={isMobile}
          showLoad={showLoad}
          showTime={showTime}
          orderRequestStatus={orderSummaryData.status}
          quantity={orderSummaryData.requestData?.quantity ?? collection.capacity.quantity}
          quantityType={collection.capacity.quantityType}
          quantityUom={orderSummaryData.requestData?.quantityUom ?? quantityUom}
          collectionDate={orderSummaryData.requestData?.deliveryDate ?? collection.deliveryDate}
          collectionTime={orderSummaryData.requestData?.deliveryTime ?? collection.deliveryTime}
          haulierInformation={orderSummaryData.requestData?.haulerInfo ?? collection.haulerInfo}
          customerReference={
            orderSummaryData.requestData?.customerReference ?? collection.customerReference
          }
          driverInstructions={
            orderSummaryData.requestData?.additionalDriverInfo?.driverInstructions ??
            collection.additionalDriverInfo?.driverInstructions
          }
        />
      ) : null}
    </>
  )
}
